import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, EnumDecoder, field, IntegerDecoder, MapDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { Premise } from './addresses/Premise.js';
import { DNSRecord } from './DNSRecord.js';
import { StamhoofdFilterDecoder } from './filters/FilteredRequest.js';
import { FilterWrapperMarker, unwrapFilter } from './filters/StamhoofdFilter.js';
import { MemberResponsibility } from './MemberResponsibility.js';
import { RecordAnswerDecoder } from './members/records/RecordAnswer.js';
import { OrganizationEmail } from './OrganizationEmail.js';
import { PayconiqAccount, PrivatePaymentConfiguration } from './PaymentConfiguration.js';
import { PaymentMethod } from './PaymentMethod.js';
import { PaymentProvider } from './PaymentProvider.js';
import { PermissionRoleDetailed, PermissionRoleForResponsibility } from './PermissionRole.js';
import { upgradePriceFrom2To4DecimalPlaces } from './upgradePriceFrom2To4DecimalPlaces.js';
import { OrganizationInvoiceSettings } from './OrganizationInvoiceSettings.js';
export class CreditItem extends AutoEncoder {
    /**
     * Credits in cents
     */
    change = 0;
    /**
     * Credits in cents
     */
    date = new Date();
    /**
     * Credits in cents
     */
    description;
    /**
     * tmp fix
     */
    static patchIdentifier() {
        return StringDecoder;
    }
}
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], CreditItem.prototype, "change", void 0);
__decorate([
    field({ decoder: DateDecoder })
], CreditItem.prototype, "date", void 0);
__decorate([
    field({ decoder: StringDecoder })
], CreditItem.prototype, "description", void 0);
export var MollieStatus;
(function (MollieStatus) {
    MollieStatus["NeedsData"] = "NeedsData";
    MollieStatus["InReview"] = "InReview";
    MollieStatus["Completed"] = "Completed";
})(MollieStatus || (MollieStatus = {}));
export var AcquisitionType;
(function (AcquisitionType) {
    AcquisitionType["Recommended"] = "Recommended";
    AcquisitionType["Seen"] = "Seen";
    AcquisitionType["SocialMedia"] = "SocialMedia";
    AcquisitionType["Search"] = "Search";
    AcquisitionType["Other"] = "Other";
})(AcquisitionType || (AcquisitionType = {}));
export var MollieProfileMode;
(function (MollieProfileMode) {
    MollieProfileMode["Test"] = "test";
    MollieProfileMode["Live"] = "live";
})(MollieProfileMode || (MollieProfileMode = {}));
export var MollieProfileStatus;
(function (MollieProfileStatus) {
    MollieProfileStatus["Unverified"] = "unverified";
    MollieProfileStatus["Verified"] = "verified";
    MollieProfileStatus["Blocked"] = "blocked";
})(MollieProfileStatus || (MollieProfileStatus = {}));
export class MollieProfile extends AutoEncoder {
    id;
    mode;
    name;
    website;
    status;
}
__decorate([
    field({ decoder: StringDecoder })
], MollieProfile.prototype, "id", void 0);
__decorate([
    field({ decoder: new EnumDecoder(MollieProfileMode) })
], MollieProfile.prototype, "mode", void 0);
__decorate([
    field({ decoder: StringDecoder })
], MollieProfile.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder })
], MollieProfile.prototype, "website", void 0);
__decorate([
    field({ decoder: new EnumDecoder(MollieProfileStatus) })
], MollieProfile.prototype, "status", void 0);
export class MollieOnboarding extends AutoEncoder {
    canReceivePayments = false;
    canReceiveSettlements = false;
    status;
}
__decorate([
    field({ decoder: BooleanDecoder })
], MollieOnboarding.prototype, "canReceivePayments", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], MollieOnboarding.prototype, "canReceiveSettlements", void 0);
__decorate([
    field({ decoder: new EnumDecoder(MollieStatus) })
], MollieOnboarding.prototype, "status", void 0);
export class BuckarooSettings extends AutoEncoder {
    key = '';
    secret = '';
    paymentMethods = [PaymentMethod.Bancontact, PaymentMethod.iDEAL, PaymentMethod.Payconiq];
}
__decorate([
    field({ decoder: StringDecoder })
], BuckarooSettings.prototype, "key", void 0);
__decorate([
    field({ decoder: StringDecoder })
], BuckarooSettings.prototype, "secret", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(new EnumDecoder(PaymentMethod)) })
], BuckarooSettings.prototype, "paymentMethods", void 0);
export class BalanceNotificationSettings extends AutoEncoder {
    enabled = false;
    emailId = null;
    maximumReminderEmails = 3;
    /**
     * Minimum days between emails
     */
    minimumDaysBetween = 5;
    /**
     * Which contacts to use for balances from other organizations
     */
    organizationContactsFilter = {};
    getOrganizationContactsFilterResponsibilityIds() {
        const f = this.organizationContactsFilter ?? {};
        const unwrapped = unwrapFilter(f, {
            meta: {
                responsibilityIds: {
                    $in: FilterWrapperMarker,
                },
            },
        });
        if (Array.isArray(unwrapped.markerValue) && unwrapped.markerValue.length && unwrapped.markerValue.every(v => typeof v === 'string')) {
            return unwrapped.markerValue;
        }
        return [];
    }
}
__decorate([
    field({ decoder: BooleanDecoder }),
    field({ decoder: BooleanDecoder, version: 363, upgrade: () => (false) }) // Force reset to false
], BalanceNotificationSettings.prototype, "enabled", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], BalanceNotificationSettings.prototype, "emailId", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 355 })
], BalanceNotificationSettings.prototype, "maximumReminderEmails", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 355 })
], BalanceNotificationSettings.prototype, "minimumDaysBetween", void 0);
__decorate([
    field({ decoder: StamhoofdFilterDecoder, version: 363, nullable: true })
], BalanceNotificationSettings.prototype, "organizationContactsFilter", void 0);
export class OrganizationPrivateMetaData extends AutoEncoder {
    /**
     * @deprecated
     */
    privateKey = null;
    /**
     * DNS records that need to be set in order to activate mail domain and registration domain
     */
    dnsRecords = [];
    /**
     * Register domain that is awaiting validation
     */
    pendingRegisterDomain = null;
    /**
     * Mail domain that is awaiting validation
     */
    pendingMailDomain = null;
    /**
     * The mail from domain that should be used (if validated).
     * This domain should have the SPF record and MX records needed to handle bounces
     */
    mailFromDomain = null;
    /**
     * Mail domain that is used to send e-mails. You can't set this directly, the server will set this value as soon as the domain has been validated.
     */
    mailDomain = null;
    mailDomainActive = false;
    invoiceSettings;
    /**
     * E-mail addresses that an organization can send from (or reply-to)
     */
    emails = [];
    /**
     * Balance reminder email settings
     */
    balanceNotificationSettings = BalanceNotificationSettings.create({});
    /**
     * @deprecated
     * Credits in cents
     */
    credits = [];
    // readonly
    mollieOnboarding = null;
    mollieProfile = null;
    /**
     * When set, Buckaroo has priority over Mollie as a payment provider
     */
    buckarooSettings = null;
    registrationPaymentConfiguration = PrivatePaymentConfiguration.create({});
    /**
     * @deprecated
     */
    get registrationProviderConfiguration() {
        return this.registrationPaymentConfiguration;
    }
    featureFlags = [];
    /**
     * Null = use environment default
     */
    useTestPayments = null;
    // Only set for admins
    payconiqAccounts = [];
    get payconiqApiKey() {
        if (this.payconiqAccounts.length === 0) {
            return null;
        }
        return this.payconiqAccounts[0].apiKey;
    }
    acquisitionTypes = [];
    roles = [];
    responsibilities = [];
    /**
     * Link responsibility type ids to roles that should be auto-applied for users linked to members with those responsibilities
     */
    inheritedResponsibilityRoles = [];
    billingContact = null;
    /**
     * @deprecated
     * Moved to public meta data. Do not use.
     */
    VATNumber = null;
    premises = [];
    recordAnswers = new Map();
    get actualTestPayments() {
        if (this.useTestPayments !== null) {
            return this.useTestPayments;
        }
        return STAMHOOFD.environment !== 'production';
    }
    getPaymentProviderFor(method, stripeAccountMeta) {
        if (method === PaymentMethod.Unknown || method === PaymentMethod.Transfer || method === PaymentMethod.PointOfSale) {
            return null;
        }
        if (this.payconiqApiKey && method === PaymentMethod.Payconiq) {
            return PaymentProvider.Payconiq;
        }
        if (stripeAccountMeta) {
            if (stripeAccountMeta.paymentMethods.includes(method)) {
                return PaymentProvider.Stripe;
            }
        }
        // Is Buckaroo setup?
        if (this.buckarooSettings !== null) {
            if (this.buckarooSettings.paymentMethods.includes(method)) {
                return PaymentProvider.Buckaroo;
            }
        }
        if ((this.mollieOnboarding && (this.mollieOnboarding?.canReceivePayments || this.actualTestPayments)) && (method == PaymentMethod.Bancontact || method == PaymentMethod.iDEAL || method == PaymentMethod.CreditCard)) {
            return PaymentProvider.Mollie;
        }
        return null;
    }
    get removedPrivateKeys() {
        const c = this.clone();
        for (const account of c.payconiqAccounts) {
            account.apiKey = PayconiqAccount.placeholderApiKey;
        }
        return c;
    }
}
__decorate([
    field({ decoder: StringDecoder, version: 149, nullable: true, optional: true })
], OrganizationPrivateMetaData.prototype, "privateKey", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(DNSRecord), version: 6 })
], OrganizationPrivateMetaData.prototype, "dnsRecords", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 84 })
], OrganizationPrivateMetaData.prototype, "pendingRegisterDomain", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 6 })
], OrganizationPrivateMetaData.prototype, "pendingMailDomain", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 84 })
], OrganizationPrivateMetaData.prototype, "mailFromDomain", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 6 })
], OrganizationPrivateMetaData.prototype, "mailDomain", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 8 })
], OrganizationPrivateMetaData.prototype, "mailDomainActive", void 0);
__decorate([
    field({ decoder: OrganizationInvoiceSettings, version: 398 })
], OrganizationPrivateMetaData.prototype, "invoiceSettings", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(OrganizationEmail), version: 9 })
], OrganizationPrivateMetaData.prototype, "emails", void 0);
__decorate([
    field({ decoder: BalanceNotificationSettings, version: 355 })
], OrganizationPrivateMetaData.prototype, "balanceNotificationSettings", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(CreditItem), version: 23, optional: true })
], OrganizationPrivateMetaData.prototype, "credits", void 0);
__decorate([
    field({ decoder: MollieOnboarding, nullable: true, version: 27 })
], OrganizationPrivateMetaData.prototype, "mollieOnboarding", void 0);
__decorate([
    field({ decoder: MollieProfile, nullable: true, version: 200 })
], OrganizationPrivateMetaData.prototype, "mollieProfile", void 0);
__decorate([
    field({ decoder: BuckarooSettings, nullable: true, version: 152 })
], OrganizationPrivateMetaData.prototype, "buckarooSettings", void 0);
__decorate([
    field({ decoder: PrivatePaymentConfiguration, version: 176, field: 'registrationProviderConfiguration' }),
    field({ decoder: PrivatePaymentConfiguration, version: 204 })
], OrganizationPrivateMetaData.prototype, "registrationPaymentConfiguration", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder), version: 161 })
], OrganizationPrivateMetaData.prototype, "featureFlags", void 0);
__decorate([
    field({ decoder: BooleanDecoder, nullable: true, version: 154 })
], OrganizationPrivateMetaData.prototype, "useTestPayments", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 29, field: 'payconiqApiKey' }),
    field({
        decoder: new ArrayDecoder(PayconiqAccount),
        version: 206,
        upgrade: (oldValue) => {
            if (oldValue === null) {
                return [];
            }
            return [PayconiqAccount.create({ apiKey: oldValue })];
        },
        downgrade: (newValue) => {
            if (newValue.length === 0) {
                return null;
            }
            return newValue[0].apiKey;
        },
    })
], OrganizationPrivateMetaData.prototype, "payconiqAccounts", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(new EnumDecoder(AcquisitionType)), version: 56 })
], OrganizationPrivateMetaData.prototype, "acquisitionTypes", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(PermissionRoleDetailed), version: 60 })
], OrganizationPrivateMetaData.prototype, "roles", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(MemberResponsibility), version: 280 })
], OrganizationPrivateMetaData.prototype, "responsibilities", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(PermissionRoleForResponsibility), version: 280 })
], OrganizationPrivateMetaData.prototype, "inheritedResponsibilityRoles", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 88 })
], OrganizationPrivateMetaData.prototype, "billingContact", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 86, optional: true })
], OrganizationPrivateMetaData.prototype, "VATNumber", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Premise), version: 319 })
], OrganizationPrivateMetaData.prototype, "premises", void 0);
__decorate([
    field({
        decoder: new MapDecoder(StringDecoder, RecordAnswerDecoder),
        version: 358,
    })
], OrganizationPrivateMetaData.prototype, "recordAnswers", void 0);
//# sourceMappingURL=OrganizationPrivateMetaData.js.map